import type { ReportTemplateCategory, StoredReportTemplate } from '../../lib/api';
import type { ComboboxOption } from './CatalogCombobox';

/** 미분류 그룹 헤더 라벨 — 분류 행이 아니라 `categoryId = null`의 표시명(결정 2026-09-20 §3). */
export const UNCATEGORIZED_LABEL = '미분류';

/** 분류 트리의 한 그룹 — 분류 하나(또는 미분류) + 소속 양식. */
export interface TemplateGroup {
  /** 분류 id — null = 미분류(`categoryId = null`). */
  id: string | null;
  name: string;
  /**
   * 분류 목록(`categories`)에 없는 categoryId를 가리키는 양식들의 그룹(목록 미도착·방금 삭제됨).
   * 응답 동봉 `category.name`으로 이름을 세우고 분류 목록 뒤·미분류 앞에 둔다 — 드롭 대상은 아니다.
   */
  orphan: boolean;
  templates: StoredReportTemplate[];
}

/**
 * 양식을 분류별 그룹으로 묶는 **순서 정본** — 선택기 옵션(`buildTemplateOptions`)과 탐색기 트리
 * (`buildExplorerTree`)가 같은 규칙을 공유한다:
 *   1) 분류는 `categories`의 순서(서버가 sortOrder, name 순으로 내려준다) — 양식이 없는 분류도 포함
 *      (탐색기에서 빈 분류가 드롭 대상이 되어야 한다)
 *   2) 분류 목록에 없는 categoryId 그룹(orphan)은 분류 뒤·미분류 앞
 *   3) 「미분류」(categoryId null)는 **마지막** — 항상 존재(0건이어도 드롭 대상)
 *   4) 같은 그룹 안에서는 `templates`의 순서(서버 생성순)를 유지
 */
export function groupTemplatesByCategory(
  templates: readonly StoredReportTemplate[],
  categories: readonly ReportTemplateCategory[],
): TemplateGroup[] {
  const byCategory = new Map<string, StoredReportTemplate[]>();
  const uncategorized: StoredReportTemplate[] = [];
  for (const t of templates) {
    if (!t.categoryId) {
      uncategorized.push(t);
      continue;
    }
    const arr = byCategory.get(t.categoryId);
    if (arr) arr.push(t);
    else byCategory.set(t.categoryId, [t]);
  }

  const out: TemplateGroup[] = [];
  const consumed = new Set<string>();
  for (const c of categories) {
    consumed.add(c.id);
    out.push({ id: c.id, name: c.name, orphan: false, templates: byCategory.get(c.id) ?? [] });
  }
  for (const [categoryId, arr] of byCategory) {
    if (consumed.has(categoryId)) continue;
    out.push({
      id: categoryId,
      name: arr[0]?.category?.name ?? UNCATEGORIZED_LABEL,
      orphan: true,
      templates: arr,
    });
  }
  out.push({ id: null, name: UNCATEGORIZED_LABEL, orphan: false, templates: uncategorized });
  return out;
}

/**
 * 템플릿 선택기(CatalogCombobox) 옵션 — 분류별 그룹 헤더로 묶는다.
 *
 * `CatalogCombobox`는 `group`이 같은 항목을 헤더로 묶되 **옵션 배열의 선언 순서**로 헤더를 세운다
 * (`groups` useMemo — 첫 등장 순). 순서는 `groupTemplatesByCategory`가 정본이고, 여기서는 양식이
 * 없는 그룹을 건너뛴다(옵션이 없는 헤더는 콤보박스에 존재할 수 없다).
 *
 * 양식이 가리키는 분류가 `categories`에 없으면(분류 목록이 아직 안 왔거나 방금 삭제됨) 응답에 동봉된
 * `category.name`으로 그룹을 만들어 **분류 목록 뒤·미분류 앞**에 둔다 — 그 양식을 「미분류」로 잘못
 * 보이게 하지 않기 위해서다.
 */
export function buildTemplateOptions(
  templates: readonly StoredReportTemplate[],
  categories: readonly ReportTemplateCategory[],
): ComboboxOption[] {
  const out: ComboboxOption[] = [];
  for (const g of groupTemplatesByCategory(templates, categories)) {
    for (const t of g.templates) out.push({ value: t.key, label: t.name, key: t.key, group: g.name });
  }
  return out;
}

/** 탐색기 공개 필터 — 전체 / 공개(enabled) / 비공개. */
export type ExplorerVisibility = 'all' | 'enabled' | 'disabled';

export interface ExplorerTreeGroup extends TemplateGroup {
  /** 필터 적용 전 소속 양식 수 — 헤더 건수(검색 중엔 `templates.length / total`). */
  total: number;
  /**
   * 검색·필터 중이고 매치가 0건이라 접어 둘 그룹(결정 §2 「빈 분류는 검색 중엔 접힘」). 필터가 없을
   * 땐 항상 false — 양식이 0개인 분류도 펼쳐 보여야 드롭 대상으로 쓸 수 있다.
   */
  collapsedByFilter: boolean;
}

export interface ExplorerTreeOptions {
  /** 이름·키 부분일치(대소문자 무시). 공백만이면 필터 없음. */
  query?: string;
  visibility?: ExplorerVisibility;
}

/** 검색 질의 정규화 — trim + 소문자. 빈 문자열이면 필터 없음. */
export function normalizeExplorerQuery(query: string | undefined): string {
  return (query ?? '').trim().toLowerCase();
}

/** 양식 하나가 탐색기 필터(검색·공개)를 통과하는가 — 트리 구성과 드래그 다중선택 판정이 공유. */
export function matchesExplorerFilter(
  t: StoredReportTemplate,
  opts: { normalizedQuery: string; visibility: ExplorerVisibility },
): boolean {
  if (opts.visibility === 'enabled' && !t.enabled) return false;
  if (opts.visibility === 'disabled' && t.enabled) return false;
  if (!opts.normalizedQuery) return true;
  const q = opts.normalizedQuery;
  return t.name.toLowerCase().includes(q) || t.key.toLowerCase().includes(q);
}

/**
 * 양식 탐색기 트리 — `groupTemplatesByCategory` 순서 위에 검색·공개 필터를 얹는다(결정 2026-09-20 §2).
 * 그룹은 필터와 무관하게 **전부** 남긴다(분류 헤더가 드롭 대상이라 사라지면 안 된다). 매치 0건인
 * 그룹은 필터 중일 때만 `collapsedByFilter`로 표시한다.
 */
export function buildExplorerTree(
  templates: readonly StoredReportTemplate[],
  categories: readonly ReportTemplateCategory[],
  opts: ExplorerTreeOptions = {},
): ExplorerTreeGroup[] {
  const normalizedQuery = normalizeExplorerQuery(opts.query);
  const visibility = opts.visibility ?? 'all';
  const filtering = normalizedQuery !== '' || visibility !== 'all';
  return groupTemplatesByCategory(templates, categories).map((g) => {
    const filtered = filtering
      ? g.templates.filter((t) => matchesExplorerFilter(t, { normalizedQuery, visibility }))
      : g.templates;
    return {
      ...g,
      templates: filtered,
      total: g.templates.length,
      collapsedByFilter: filtering && filtered.length === 0,
    };
  });
}
